import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import { Banner } from "../settings/settingsUi";

const OPTIONS = [
  {
    id: "light",
    label: "Light",
    desc: "Bright surfaces and high contrast for daytime use.",
    icon: "ri-sun-line",
  },
  {
    id: "dark",
    label: "Dark",
    desc: "Reduced glare for low-light environments.",
    icon: "ri-moon-line",
  },
  {
    id: "system",
    label: "System",
    desc: "Match this device's light or dark preference automatically.",
    icon: "ri-computer-line",
  },
];

export default function AppearanceSettingsSection({
  savedTheme,
  onSaveTheme,
  saving,
  loadError,
}) {
  const { theme, setTheme } = useTheme();
  const [draftTheme, setDraftTheme] = useState(savedTheme || theme);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setDraftTheme(savedTheme || theme);
  }, [savedTheme, theme]);

  const dirty = draftTheme !== (savedTheme || theme);

  const handleSave = async () => {
    if (!dirty) return;
    const result = await onSaveTheme(draftTheme);
    if (!result?.ok) return;
    setTheme(result.theme || draftTheme);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
    toast.success("Appearance saved", {
      description: "Your theme preference has been saved across all your workspaces.",
    });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Appearance</h3>
        <p className="mt-1 text-sm text-slate-500">
          Theme applies to every workspace you belong to. Click <strong className="font-semibold text-slate-700 dark:text-slate-300">Save appearance</strong> to persist your choice.
        </p>
      </div>

      {loadError && <Banner type="err" text={loadError} />}

      <div className="ui-card-static space-y-5 !p-6 sm:!p-8">
        <div className="grid gap-3 sm:grid-cols-3">
          {OPTIONS.map((opt) => {
            const picked = draftTheme === opt.id;
            const saved = (savedTheme || theme) === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDraftTheme(opt.id)}
                disabled={saving}
                className={`flex flex-col rounded-2xl border p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 sm:p-5 dark:focus-visible:ring-offset-slate-900 ${
                  picked
                    ? "border-teal-500/80 bg-gradient-to-br from-teal-50 to-cyan-50/80 shadow-md shadow-teal-900/10 ring-2 ring-teal-500/30 dark:from-teal-950/50 dark:to-slate-900 dark:ring-teal-500/40"
                    : "border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-600 dark:bg-slate-900/60 dark:hover:border-slate-500"
                }`}
              >
                <span
                  className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl text-xl ${
                    picked
                      ? "bg-teal-600 text-white shadow-md"
                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                  aria-hidden
                >
                  <i className={opt.icon} />
                </span>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{opt.label}</span>
                <span className="mt-1.5 text-xs leading-relaxed text-slate-500">{opt.desc}</span>
                {picked && (
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                    <i className={saved && !dirty ? "ri-check-line text-sm" : "ri-focus-3-line text-sm"} aria-hidden />
                    {saved && !dirty ? "Saved & active" : "Selected — save to apply"}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {dirty && (
          <p className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs font-medium text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
            You have unsaved changes. Click <strong className="font-semibold">Save appearance</strong> to switch the interface.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-5 dark:border-slate-700">
          <button
            type="button"
            className="ui-btn-primary"
            disabled={!dirty || saving}
            onClick={() => void handleSave()}
          >
            {saving ? "Saving…" : "Save appearance"}
          </button>
          {justSaved && !dirty && (
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Saved.</span>
          )}
        </div>
      </div>
    </div>
  );
}
